/**
 * ProgressionCycle.js - VERSÃO PREMIUM
 * 
 * Visualização épica do ciclo de progressão da memória
 * Design de jornada com partículas, glow e animações ricas
 */

import React, { useRef, useEffect } from 'react';
import { View, StyleSheet, Animated, Easing, Dimensions, Platform } from 'react-native';
import { Text } from '../ui/AppText';
import { LinearGradient } from 'expo-linear-gradient';
import Svg, { Path, Circle, Defs, LinearGradient as SvgGradient, Stop, G, Rect } from 'react-native-svg';
import { SHORT_TERM_COLORS } from '../../constants/categories';

const { width: SCREEN_WIDTH } = Dimensions.get('window');

const ProgressionCycle = ({
    currentStage = 'short',
    shortCount = 0,
    mediumCount = 0,
    longCount = 0,
    compact = false,
    showAnimation = true,
}) => {
    // Animações
    const pulseAnim = useRef(new Animated.Value(1)).current;
    const glowAnim = useRef(new Animated.Value(0.5)).current;
    const particleAnim = useRef(new Animated.Value(0)).current;
    const flowAnim = useRef(new Animated.Value(0)).current;
    const bounceAnim1 = useRef(new Animated.Value(0)).current;
    const bounceAnim2 = useRef(new Animated.Value(0)).current;
    const bounceAnim3 = useRef(new Animated.Value(0)).current;

    // Pulse no estágio atual
    useEffect(() => {
        if (showAnimation) {
            const pulse = Animated.loop(
                Animated.sequence([
                    Animated.timing(pulseAnim, { toValue: 1.08, duration: 1200, useNativeDriver: Platform.OS !== 'web' }),
                    Animated.timing(pulseAnim, { toValue: 1, duration: 1200, useNativeDriver: Platform.OS !== 'web' }),
                ])
            );
            pulse.start();
            return () => pulse.stop();
        }
    }, [showAnimation, pulseAnim]);

    // Glow animation
    useEffect(() => {
        const glow = Animated.loop(
            Animated.sequence([
                Animated.timing(glowAnim, { toValue: 1, duration: 1500, useNativeDriver: Platform.OS !== 'web' }),
                Animated.timing(glowAnim, { toValue: 0.4, duration: 1500, useNativeDriver: Platform.OS !== 'web' }),
            ])
        );
        glow.start();
        return () => glow.stop();
    }, [glowAnim]);

    // Flow animation (partículas movendo entre estágios)
    useEffect(() => {
        const flow = Animated.loop(
            Animated.timing(flowAnim, {
                toValue: 1,
                duration: 3000,
                easing: Easing.linear,
                useNativeDriver: Platform.OS !== 'web',
            })
        );
        flow.start();
        return () => flow.stop();
    }, [flowAnim]);

    // Bounce staggered
    useEffect(() => {
        const bounceSequence = (anim, delay) => {
            Animated.loop(
                Animated.sequence([
                    Animated.delay(delay),
                    Animated.timing(anim, { toValue: -5, duration: 300, useNativeDriver: Platform.OS !== 'web' }),
                    Animated.spring(anim, { toValue: 0, tension: 200, friction: 5, useNativeDriver: Platform.OS !== 'web' }),
                    Animated.delay(2500),
                ])
            ).start();
        };

        bounceSequence(bounceAnim1, 0);
        bounceSequence(bounceAnim2, 400);
        bounceSequence(bounceAnim3, 800);
    }, [bounceAnim1, bounceAnim2, bounceAnim3]);

    const stages = [
        {
            id: 'short',
            label: 'Curto Prazo',
            sublabel: 'Erros Recentes',
            icon: '⚡',
            emoji: '🧠',
            color: SHORT_TERM_COLORS.primary,
            gradientColors: ['#FF6B6B', '#FF8E53'],
            count: shortCount,
            requirement: '5 acertos',
            bounceAnim: bounceAnim1,
        },
        {
            id: 'medium',
            label: 'Médio Prazo',
            sublabel: 'Em Consolidação',
            icon: '📈',
            emoji: '💪',
            color: '#5B8FF9',
            gradientColors: ['#5B8FF9', '#667eea'],
            count: mediumCount,
            requirement: 'Repetição',
            bounceAnim: bounceAnim2,
        },
        {
            id: 'long',
            label: 'Dominado',
            sublabel: 'Memória Permanente',
            icon: '🏆',
            emoji: '🌟',
            color: '#51CF66',
            gradientColors: ['#51CF66', '#40C057'],
            count: longCount,
            requirement: 'Consolidado',
            bounceAnim: bounceAnim3,
        },
    ];

    const totalQuestions = shortCount + mediumCount + longCount;
    const dominatePercent = totalQuestions > 0 ? Math.round((longCount / totalQuestions) * 100) : 0;

    if (compact) {
        return (
            <View style={styles.compactContainer}>
                {stages.map((stage, index) => (
                    <View key={stage.id} style={styles.compactItem}>
                        <View style={[styles.compactCircle, { backgroundColor: stage.color }]}>
                            <Text style={styles.compactIcon}>{stage.icon}</Text>
                        </View>
                        <Text style={[styles.compactCount, { color: stage.color }]}>{stage.count}</Text>
                        {index < stages.length - 1 && (
                            <View style={styles.compactArrow}>
                                <Text style={styles.compactArrowText}>→</Text>
                            </View>
                        )}
                    </View>
                ))}
            </View>
        );
    }

    return (
        <View style={styles.container}>
            {/* Header épico */}
            <View style={styles.headerSection}>
                <View style={styles.headerLeft}>
                    <Text style={styles.mainTitle}>🧠 Jornada do Conhecimento</Text>
                    <Text style={styles.mainSubtitle}>
                        {dominatePercent}% do conteúdo dominado
                    </Text>
                </View>
                <View style={styles.headerBadge}>
                    <LinearGradient
                        colors={['#51CF66', '#40C057']}
                        style={styles.badgeGradient}
                    >
                        <Text style={styles.badgeText}>{longCount}</Text>
                        <Text style={styles.badgeLabel}>✓</Text>
                    </LinearGradient>
                </View>
            </View>

            {/* Visualização dos estágios */}
            <View style={styles.pathContainer}>
                {/* Estágios em linha */}
                <View style={styles.stagesOverlay}>
                    {stages.map((stage, index) => {
                        const isActive = stage.id === currentStage;

                        return (
                            <React.Fragment key={stage.id}>
                                <Animated.View
                                    style={[
                                        styles.stageCircleWrapper,
                                        {
                                            transform: [
                                                { translateY: stage.bounceAnim },
                                                isActive ? { scale: pulseAnim } : { scale: 1 },
                                            ]
                                        },
                                    ]}
                                >
                                    {/* Anel de glow para ativo */}
                                    {isActive && (
                                        <Animated.View
                                            style={[
                                                styles.activeGlow,
                                                {
                                                    backgroundColor: stage.color + '30',
                                                    opacity: glowAnim,
                                                }
                                            ]}
                                        />
                                    )}

                                    {/* Círculo principal */}
                                    <LinearGradient
                                        colors={stage.gradientColors}
                                        style={[
                                            styles.stageCircle,
                                            !isActive && styles.stageCircleInactive,
                                        ]}
                                    >
                                        <Text style={styles.stageIcon}>{stage.icon}</Text>
                                        <Text style={styles.stageCount}>{stage.count}</Text>
                                    </LinearGradient>

                                    {/* Labels */}
                                    <Text style={[styles.stageLabel, isActive && { color: stage.color }]}>
                                        {stage.label}
                                    </Text>
                                    <Text style={styles.stageSublabel}>{stage.sublabel}</Text>

                                    {/* Requisito */}
                                    <View style={[styles.requirementBadge, { backgroundColor: stage.color + '20' }]}>
                                        <Text style={[styles.requirementText, { color: stage.color }]}>
                                            {stage.requirement}
                                        </Text>
                                    </View>
                                </Animated.View>

                                {/* Seta entre estágios */}
                                {index < stages.length - 1 && (
                                    <View style={styles.arrowBetween}>
                                        <Text style={styles.arrowBetweenText}>→</Text>
                                    </View>
                                )}
                            </React.Fragment>
                        );
                    })}
                </View>
            </View>

            {/* Estatística geral */}
            <View style={styles.statsBar}>
                <View style={styles.statBarBackground}>
                    {/* Segmentos coloridos */}
                    <View
                        style={[
                            styles.statSegment,
                            {
                                width: `${totalQuestions > 0 ? (shortCount / totalQuestions) * 100 : 33}%`,
                                backgroundColor: '#FF6B6B',
                            }
                        ]}
                    />
                    <View
                        style={[
                            styles.statSegment,
                            {
                                width: `${totalQuestions > 0 ? (mediumCount / totalQuestions) * 100 : 33}%`,
                                backgroundColor: '#5B8FF9',
                            }
                        ]}
                    />
                    <View
                        style={[
                            styles.statSegment,
                            {
                                width: `${totalQuestions > 0 ? (longCount / totalQuestions) * 100 : 34}%`,
                                backgroundColor: '#51CF66',
                            }
                        ]}
                    />
                </View>

                {/* Legenda */}
                <View style={styles.legendRow}>
                    <View style={styles.legendItem}>
                        <View style={[styles.legendDot, { backgroundColor: '#FF6B6B' }]} />
                        <Text style={styles.legendText}>Curto</Text>
                    </View>
                    <View style={styles.legendItem}>
                        <View style={[styles.legendDot, { backgroundColor: '#5B8FF9' }]} />
                        <Text style={styles.legendText}>Médio</Text>
                    </View>
                    <View style={styles.legendItem}>
                        <View style={[styles.legendDot, { backgroundColor: '#51CF66' }]} />
                        <Text style={styles.legendText}>Dominado</Text>
                    </View>
                </View>
            </View>
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        backgroundColor: 'rgba(255,255,255,0.03)',
        borderRadius: 24,
        padding: 20,
        marginHorizontal: 16,
        marginVertical: 12,
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.08)',
    },
    headerSection: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 24,
    },
    headerLeft: {
        flex: 1,
    },
    mainTitle: {
        fontSize: 18,
        fontWeight: '800',
        color: '#E8EAED',
        marginBottom: 4,
    },
    mainSubtitle: {
        fontSize: 13,
        color: 'rgba(255,255,255,0.5)',
    },
    headerBadge: {
        overflow: 'hidden',
        borderRadius: 16,
    },
    badgeGradient: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 12,
        paddingVertical: 8,
        gap: 4,
    },
    badgeText: {
        fontSize: 18,
        fontWeight: '800',
        color: '#FFFFFF',
    },
    badgeLabel: {
        fontSize: 14,
        color: '#FFFFFF',
    },
    pathContainer: {
        marginBottom: 20,
    },
    stagesOverlay: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        paddingHorizontal: 10,
    },
    stageCircleWrapper: {
        alignItems: 'center',
        flex: 1,
    },
    activeGlow: {
        position: 'absolute',
        top: -10,
        width: 90,
        height: 90,
        borderRadius: 45,
    },
    stageCircle: {
        width: 70,
        height: 70,
        borderRadius: 35,
        justifyContent: 'center',
        alignItems: 'center',
        marginBottom: 8,
    },
    stageCircleInactive: {
        opacity: 0.6,
    },
    stageIcon: {
        fontSize: 22,
    },
    stageCount: {
        fontSize: 16,
        fontWeight: '800',
        color: '#FFFFFF',
        marginTop: 2,
    },
    stageLabel: {
        fontSize: 12,
        fontWeight: '700',
        color: '#E8EAED',
        textAlign: 'center',
    },
    stageSublabel: {
        fontSize: 10,
        color: 'rgba(255,255,255,0.4)',
        textAlign: 'center',
        marginTop: 2,
    },
    requirementBadge: {
        marginTop: 6,
        paddingHorizontal: 8,
        paddingVertical: 3,
        borderRadius: 6,
    },
    requirementText: {
        fontSize: 9,
        fontWeight: '700',
    },
    arrowBetween: {
        justifyContent: 'center',
        alignItems: 'center',
        paddingHorizontal: 4,
    },
    arrowBetweenText: {
        color: 'rgba(255,255,255,0.3)',
        fontSize: 20,
        fontWeight: '700',
    },
    statsBar: {
        marginTop: 8,
    },
    statBarBackground: {
        height: 8,
        borderRadius: 4,
        flexDirection: 'row',
        overflow: 'hidden',
        backgroundColor: 'rgba(255,255,255,0.1)',
    },
    statSegment: {
        height: '100%',
    },
    legendRow: {
        flexDirection: 'row',
        justifyContent: 'center',
        gap: 20,
        marginTop: 12,
    },
    legendItem: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
    },
    legendDot: {
        width: 8,
        height: 8,
        borderRadius: 4,
    },
    legendText: {
        fontSize: 11,
        color: 'rgba(255,255,255,0.5)',
    },
    // Compact styles
    compactContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: 8,
    },
    compactItem: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    compactCircle: {
        width: 28,
        height: 28,
        borderRadius: 14,
        justifyContent: 'center',
        alignItems: 'center',
    },
    compactIcon: {
        fontSize: 14,
    },
    compactCount: {
        fontSize: 14,
        fontWeight: '700',
        marginLeft: 6,
    },
    compactArrow: {
        marginHorizontal: 12,
    },
    compactArrowText: {
        color: 'rgba(255,255,255,0.3)',
        fontSize: 16,
    },
});

export default ProgressionCycle;
